import { useEffect, useRef, type RefObject } from 'react';

export interface UseDismissOnOutsideClickOptions {
  /** When true, outside clicks invoke `onDismiss`. */
  isActive: boolean;
  onDismiss: () => void;
  /** Wrapper around trigger + menu (typical anchored dropdown). */
  containerRef: RefObject<HTMLElement | null>;
  /** Extra roots treated as inside (e.g. portaled menu panels). */
  insideRefs?: RefObject<HTMLElement | null>[];
}

function isInside(
  target: Node,
  containerRef: RefObject<HTMLElement | null>,
  insideRefs?: RefObject<HTMLElement | null>[],
): boolean {
  if (containerRef.current?.contains(target)) return true;
  return insideRefs?.some((ref) => ref.current?.contains(target)) ?? false;
}

/**
 * Closes menus/popovers when the user clicks outside the container (and optional inside refs).
 */
export function useDismissOnOutsideClick({
  isActive,
  onDismiss,
  containerRef,
  insideRefs,
}: UseDismissOnOutsideClickOptions): void {
  const insideRefsRef = useRef(insideRefs);

  useEffect(() => {
    insideRefsRef.current = insideRefs;
  });

  useEffect(() => {
    if (!isActive) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target || isInside(target, containerRef, insideRefsRef.current)) return;
      onDismiss();
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isActive, onDismiss, containerRef]);
}
